import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  matchPath,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "./lib/auth";
import { useTheme, type ThemeMode } from "./lib/theme";
import {
  Avatar,
  Badge,
  Button,
  cx,
  Icon,
  Modal,
  SegmentedControl,
  Skeleton,
  type IconName,
} from "./components/ui";
import { AuthPage } from "./pages/AuthPage";
import { ChooseUsernamePage } from "./pages/ChooseUsernamePage";
import { ClaimPage } from "./pages/ClaimPage";
import { JoinPage } from "./pages/JoinPage";
import { DashboardPage } from "./pages/DashboardPage";
import { CompetitionPage } from "./pages/CompetitionPage";
import { AdminPage } from "./pages/AdminPage";

export function App() {
  const { user, needsProfile, isLoading } = useAuth();
  const location = useLocation();

  /**
   * Both link flows have to work for someone with no account at all — that's the whole point of
   * a link you forward to a friend — so they're handled before the auth and profile gates below,
   * which would otherwise redirect and lose the invite.
   *
   * The token is matched here and passed down as a prop rather than read with `useParams` in the
   * page. These components render *outside* the `<Routes>` tree, so there is no matched route
   * for `useParams` to read and it returns an empty object — which silently produced an empty
   * code, a request to `/competitions/invite/`, and a 404 that looked exactly like a bad link.
   */
  const claim = matchPath("/claim/:token", location.pathname);
  if (claim?.params.token) return <ClaimPage token={claim.params.token} />;

  const join = matchPath("/join/:code", location.pathname);
  if (join?.params.code) return <JoinPage code={join.params.code} />;

  if (isLoading) return <BootSkeleton />;

  // Signed in with Firebase but no username yet — everything else waits on that.
  if (needsProfile) return <ChooseUsernamePage />;
  if (!user) return <AuthPage />;

  return (
    <div className="min-h-dvh">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[70] focus:rounded-xl focus:bg-accent focus:px-4 focus:py-2.5 focus:text-sm focus:font-semibold focus:text-accent-fg"
      >
        Skip to content
      </a>

      <TopNav />

      <main
        id="main"
        className="mx-auto max-w-6xl px-4 pb-28 pt-6 sm:px-6 sm:pb-20 sm:pt-10"
      >
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/c/:id" element={<CompetitionPage />} />
          <Route
            path="/admin"
            element={user.isSuperAdmin ? <AdminPage /> : <Navigate to="/" replace />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <MobileTabBar />
    </div>
  );
}

function BootSkeleton() {
  return (
    <div className="mx-auto max-w-5xl px-4 py-16">
      <Skeleton className="h-10 w-56" />
      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <Skeleton className="h-40" />
        <Skeleton className="h-40" />
      </div>
    </div>
  );
}

/* Header ------------------------------------------------------------------- */

function TopNav() {
  const { user } = useAuth();

  return (
    <header className="frosted safe-top sticky top-0 z-40 border-b border-line">
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-3 px-4 sm:h-[4.5rem] sm:px-6">
        <NavLink
          to="/"
          className="flex min-w-0 items-center gap-2.5 font-bold tracking-tight text-fg"
        >
          <BallMark size={30} />
          <span className="truncate text-[15px] sm:text-base">Last Man Standing</span>
        </NavLink>

        <nav className="ml-6 hidden items-center gap-1 sm:flex" aria-label="Primary">
          <NavItem to="/" icon="ball">
            Competitions
          </NavItem>
          {user?.isSuperAdmin && (
            <NavItem to="/admin" icon="shield">
              Admin
            </NavItem>
          )}
        </nav>

        <div className="ml-auto flex items-center gap-1.5">
          <ThemeButton />
          <AccountMenu />
        </div>
      </div>
    </header>
  );
}

function NavItem({ to, icon, children }: { to: string; icon: IconName; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        cx(
          "flex items-center gap-2 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors duration-200",
          isActive ? "bg-tint-strong text-fg" : "text-fg-subtle hover:bg-tint hover:text-fg",
        )
      }
    >
      <Icon name={icon} className="size-4" />
      {children}
    </NavLink>
  );
}

/** Straight light/dark flip. The three-way choice, including System, lives in the account menu
 *  — a header button that cycles through three states is a guessing game. */
function ThemeButton() {
  const { resolved, toggle } = useTheme();

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={`Switch to ${resolved === "dark" ? "light" : "dark"} mode`}
      title={`Switch to ${resolved === "dark" ? "light" : "dark"} mode`}
      className="relative grid size-10 place-items-center overflow-hidden rounded-xl text-fg-subtle transition-colors hover:bg-tint hover:text-fg"
    >
      <span
        key={resolved}
        className="animate-pop grid place-items-center"
      >
        <Icon name={resolved === "dark" ? "moon" : "sun"} className="size-5" />
      </span>
    </button>
  );
}

function AccountMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;

  const signOut = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-xl py-1 pl-1 pr-1 transition-colors hover:bg-tint sm:pr-2.5"
      >
        <Avatar name={user.displayName} color={user.avatarColor} size={34} />
        <span className="hidden min-w-0 text-left sm:block">
          <span className="block max-w-[9rem] truncate text-[13px] font-semibold leading-tight text-fg">
            {user.displayName}
          </span>
          {user.isSuperAdmin && (
            <span className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-accent-text">
              Super admin
            </span>
          )}
        </span>
        <Icon name="chevronDown" className="hidden size-4 text-fg-faint sm:block" />
      </button>

      {open && (
        <div
          role="menu"
          className="animate-pop absolute right-0 top-[calc(100%+0.5rem)] z-50 w-64 origin-top-right rounded-2xl border border-line bg-surface p-2 shadow-pop"
        >
          <div className="flex items-center gap-3 rounded-xl px-2.5 py-2.5">
            <Avatar name={user.displayName} color={user.avatarColor} size={38} />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-fg">{user.displayName}</p>
              {user.email && <p className="truncate text-[12px] text-fg-subtle">{user.email}</p>}
            </div>
          </div>

          <div className="my-2 border-t border-line" />

          <div className="px-2.5 pb-2">
            <p className="label mb-2">Appearance</p>
            <ThemeChoice />
          </div>

          <div className="my-2 border-t border-line" />

          <button
            role="menuitem"
            onClick={() => void signOut()}
            className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-sm font-medium text-fg-subtle transition-colors hover:bg-tint hover:text-fg"
          >
            <Icon name="logout" className="size-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

const THEME_OPTIONS: Array<{ value: ThemeMode; label: string; icon: IconName }> = [
  { value: "light", label: "Light", icon: "sun" },
  { value: "system", label: "Auto", icon: "monitor" },
  { value: "dark", label: "Dark", icon: "moon" },
];

function ThemeChoice() {
  const { mode, setMode } = useTheme();
  return (
    <SegmentedControl
      size="sm"
      value={mode}
      onChange={setMode}
      options={THEME_OPTIONS}
      className="w-full [&>button]:flex-1"
    />
  );
}

/* Mobile ------------------------------------------------------------------- */

function MobileTabBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [accountOpen, setAccountOpen] = useState(false);

  if (!user) return null;

  const signOut = async () => {
    setAccountOpen(false);
    await logout();
    navigate("/");
  };

  return (
    <>
      <nav
        aria-label="Primary"
        className="frosted safe-bottom fixed inset-x-0 bottom-0 z-40 flex border-t border-line px-2 pt-1.5 sm:hidden"
      >
        <MobileTab to="/" icon="ball" label="Competitions" />
        {user.isSuperAdmin && <MobileTab to="/admin" icon="shield" label="Admin" />}
        <button
          onClick={() => setAccountOpen(true)}
          className="flex min-h-14 flex-1 flex-col items-center justify-center gap-1 rounded-2xl text-[11px] font-medium text-fg-subtle transition-colors active:bg-tint"
        >
          <Avatar name={user.displayName} color={user.avatarColor} size={22} />
          <span>Account</span>
        </button>
      </nav>

      <Modal open={accountOpen} onClose={() => setAccountOpen(false)} title="Account">
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <Avatar name={user.displayName} color={user.avatarColor} size={48} />
            <div className="min-w-0">
              <p className="flex items-center gap-2 font-semibold text-fg">
                {user.displayName}
                {user.isSuperAdmin && <Badge tone="accent">Super admin</Badge>}
              </p>
              {user.email && <p className="truncate text-[13px] text-fg-subtle">{user.email}</p>}
            </div>
          </div>

          <div>
            <p className="label mb-2">Appearance</p>
            <ThemeChoice />
          </div>

          <Button variant="secondary" block onClick={() => void signOut()}>
            <Icon name="logout" className="size-4" />
            Sign out
          </Button>
        </div>
      </Modal>
    </>
  );
}

function MobileTab({ to, icon, label }: { to: string; icon: IconName; label: string }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        cx(
          "flex min-h-14 flex-1 flex-col items-center justify-center gap-1 rounded-2xl text-[11px] font-medium transition-colors",
          isActive ? "text-accent-text" : "text-fg-subtle active:bg-tint",
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon name={icon} className="size-[21px]" strokeWidth={isActive ? 2.2 : 1.8} />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  );
}

/* Brand -------------------------------------------------------------------- */

export function BallMark({ size = 26 }: { size?: number }) {
  return (
    <span
      className="relative grid shrink-0 place-items-center overflow-hidden rounded-[0.36em] bg-accent font-black text-accent-fg"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.55,
        boxShadow: "var(--t-glow)",
      }}
      aria-hidden="true"
    >
      <span className="relative z-10 font-display leading-none">L</span>
      <span className="absolute inset-y-0 left-1/2 w-px bg-black/12" />
      <span className="absolute left-1/2 top-1/2 size-[44%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-black/12" />
    </span>
  );
}
